import { z } from 'zod';
import { localeSchema, themeSchema, userRoleSchema } from './enums.js';
/**
 * Contrats d'authentification (XFILE-14) — source unique front + back.
 *
 * Gate de login : l'app est verrouillée derrière une session (cookie httpOnly
 * opaque, adossé à une table `sessions` révocable côté serveur). Multi-login :
 * plusieurs comptes empilés dans un cookie chiffré, basculables sans re-saisir de
 * mot de passe (cf. ADR-0004).
 */
/**
 * Corps de `POST /api/auth/login`. Volontairement permissif sur le mot de passe
 * (`min(1)`) : la politique de force s'applique à la CRÉATION du compte, pas au
 * login — le contrôle ici reste générique (anti-énumération).
 *
 * `stayLoggedIn` (« Rester connecté ») pilote la PERSISTANCE du cookie : `true`/omis
 * → cookie persistant (survit à la fermeture du navigateur) ; `false` → cookie de
 * session (effacé à la fermeture). `.optional()` simple (jamais `.default()`) : la
 * route dérive un JSON Schema de ce schéma au boot (Zod v4).
 */
export const loginRequestSchema = z.object({
    email: z.string().email().max(320),
    password: z.string().min(1).max(1024),
    stayLoggedIn: z.boolean().optional(),
});
/**
 * Utilisateur courant — réponse de `GET /api/auth/me` et de `POST /api/auth/login`.
 * Ne porte JAMAIS le hash du mot de passe.
 */
export const currentUserSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    role: userRoleSchema,
    locale: localeSchema,
    theme: themeSchema,
});
/**
 * Réponse de `POST /api/auth/login` (multi-login) — l'utilisateur connecté ET le
 * `slot` (petit entier) qui vient de lui être attribué dans le faisceau du
 * navigateur. Le client mémorise ce slot (par onglet) et le renvoie ensuite via
 * l'en-tête `X-Active-Slot`.
 */
export const loginResponseSchema = z.object({
    slot: z.number().int().min(0),
    user: currentUserSchema,
});
/**
 * Un compte EMPILÉ dans le faisceau — vue légère pour le sélecteur de comptes
 * (`GET /api/auth/accounts`). Ne porte pas les préférences (locale/thème) : c'est
 * une entrée de menu, pas le profil actif.
 */
export const sessionAccountSchema = z.object({
    slot: z.number().int().min(0),
    id: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    role: userRoleSchema,
});
/** Réponse de `GET /api/auth/accounts` — les comptes actuellement connectés. */
export const accountsResponseSchema = z.object({
    accounts: z.array(sessionAccountSchema),
});
/**
 * Corps de `POST /api/auth/logout`. `all: true` déconnecte TOUS les comptes
 * empilés (vide le faisceau + révoque toutes les sessions de l'utilisateur) ;
 * sinon, seul le compte du slot actif (en-tête `X-Active-Slot`) est déconnecté.
 */
export const logoutRequestSchema = z.object({
    all: z.boolean().optional().default(false),
});
/**
 * Corps de `POST /api/auth/change-password`. La politique de force (`min(8)`)
 * s'applique au NOUVEAU mot de passe, comme à la création de compte ; le mot de
 * passe actuel reste permissif (`min(1)`) — il n'est que vérifié.
 */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(1024),
    newPassword: z.string().min(8).max(1024),
});
//# sourceMappingURL=auth.js.map